import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Clock,
  Wrench,
  User,
  FileText,
  Sparkles,
  CheckCircle,
  AlertCircle,
  Camera,
  Settings,
  UserPlus,
  Save,
  X,
  Loader2,
  ClipboardList,
  AlertTriangle,
  Info,
  Calendar,
  Tag,
  ShieldCheck,
  Edit3,
  Flag,
  History,
  FilePlus,
  BookOpen,
} from "lucide-react";
import { maintenanceAPI, errorAPI, sopsAPI } from "../api";
import { useAuthStore } from "../store/authStore";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import clsx from "clsx";
import { PERMISSIONS } from "../constants/roles";

const MaintenanceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [isAssigning, setIsAssigning] = useState(false);
  const [isUpdatingSOP, setIsUpdatingSOP] = useState(false);
  const [isSopModalOpen, setIsSopModalOpen] = useState(false);
  const [selectedTech, setSelectedTech] = useState("");
  const [selectedSOP, setSelectedSOP] = useState("");
  const [selectedManualId, setSelectedManualId] = useState("");
  const [activePhoto, setActivePhoto] = useState(null);

  // Data Queries
  const {
    data: log,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["maintenance-log", id],
    queryFn: () => maintenanceAPI.getById(id).then((res) => res.data.data),
  });

  const { data: linkedBug } = useQuery({
    queryKey: ["linked-bug", log?.bug_id],
    queryFn: () => errorAPI.getById(log.bug_id).then((res) => res.data.data),
    enabled: !!log?.bug_id,
  });

  const { data: technicians } = useQuery({
    queryKey: ["technicians-list"],
    queryFn: () => maintenanceAPI.getTechnicians().then((res) => res.data.data),
    enabled:
      !!(
        user?.role === "super_admin" ||
        user?.role === "admin" ||
        user?.role === "plant_manager"
      ) || isAssigning, // MODIFICATION: also enable if isAssigning is true
  });

  const { data: availableSOPs } = useQuery({
    queryKey: ["available-sops", log?.machine_id],
    queryFn: () =>
      sopsAPI
        .getAll({ machineId: log.machine_id, limit: 100 })
        .then((res) => res.data.data),
    enabled: !!log?.machine_id && isUpdatingSOP,
  });

  // Fetch machine manuals for SOP synthesis
  const { data: machineManuals } = useQuery({
    queryKey: ["machine-manuals", log?.machine_id],
    queryFn: () =>
      maintenanceAPI
        .getMachineManuals(log.machine_id)
        .then((res) => res.data.data),
    enabled: !!log?.machine_id && isSopModalOpen,
  });

  const canManage = PERMISSIONS.MANAGE_MAINTENANCE.includes(user?.role);

  // Logic for the Knowledge Capture button
  const showStandardizeButton =
    log?.status === "completed" && log?.bug_id && !log?.sop_id && canManage;

  // Mutations
  const assignMutation = useMutation({
    mutationFn: (techId) => maintenanceAPI.assign(id, techId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-log", id] });
      toast.success("Technician assigned successfully");
      setIsAssigning(false);
    },
    onError: (err) => toast.error("Assignment failed: " + err.message),
  });

  const generateSopMutation = useMutation({
    mutationFn: (data) => maintenanceAPI.generateSopFromFix(data),
    onSuccess: (res) => {
      toast.success("AI Draft Ready for Review");
      // We pass the entire result data, which now includes sourceLogId
      navigate("/sops/new", { state: { draft: res.data.data } });
    },
  });

  const updateSopMutation = useMutation({
    mutationFn: (sopId) => maintenanceAPI.updateSop(id, sopId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-log", id] });
      toast.success("SOP updated successfully");
      setIsUpdatingSOP(false);
    },
    onError: (err) => toast.error("Failed to update SOP: " + err.message),
  });

  if (isLoading)
    return (
      <div className="flex justify-center h-96 items-center">
        <div className="w-10 h-10 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );

  if (error || !log)
    return (
      <div className="text-center py-12 text-slate-400 font-medium">
        Maintenance record not found.
      </div>
    );

  const stepsCompleted = Array.isArray(log.steps_completed)
    ? log.steps_completed
    : [];
  const sopSteps = Array.isArray(log.sop_steps) ? log.sop_steps : [];
  const partsUsed = Array.isArray(log.parts_used) ? log.parts_used : [];
  const aiPlan = log.ai_plan;
  const executionSummary = log.observations?.technical_summary;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-6 pb-12">
      {/* 1. Evidence Photo Lightbox */}
      <AnimatePresence>
        {activePhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/90 backdrop-blur-sm"
            onClick={() => setActivePhoto(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-5xl w-full bg-white p-2 rounded-3xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActivePhoto(null)}
                className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur rounded-full hover:bg-white text-slate-900 shadow-lg z-10"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={activePhoto}
                alt="Detail"
                className="w-full h-auto max-h-[85vh] object-contain rounded-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 2. SOP Synthesis Modal */}
      <AnimatePresence>
        {isSopModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: 50 }}
              animate={{ y: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-xl w-full overflow-hidden"
            >
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600 rounded-xl text-white shadow-lg shadow-blue-100">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <h2 className="font-bold text-slate-900">
                    Standardize Fix as SOP
                  </h2>
                </div>
                <button
                  onClick={() => setIsSopModalOpen(false)}
                  className="p-2 hover:bg-slate-200 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="p-8 space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">
                    Target Machine
                  </label>
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex items-center gap-3">
                    <Settings className="w-5 h-5 text-slate-400" />
                    <span className="font-bold text-slate-700">
                      {log.machine_name}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest italic flex items-center gap-2">
                    <BookOpen className="w-3 h-3" /> Select Grounding Document
                  </label>
                  <p className="text-[11px] text-slate-500 mb-2">
                    AI will cross-reference the fix steps against this technical
                    manual.
                  </p>
                  <select
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:ring-2 focus:ring-blue-500/20 outline-none"
                    value={selectedManualId}
                    onChange={(e) => setSelectedManualId(e.target.value)}
                  >
                    <option value="">Select a manual or guide...</option>
                    {machineManuals?.map((doc) => (
                      <option key={doc.id || doc._id} value={doc.id || doc._id}>
                        {doc.title}{" "}
                        {doc.documentType ? `(${doc.documentType})` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 text-amber-500 shrink-0" />
                    <p className="text-xs text-amber-800 leading-relaxed font-medium">
                      AI will analyze the{" "}
                      <strong>{stepsCompleted.length} steps</strong> documented
                      by the technician and synthesize them into a formal
                      procedure.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
                <button
                  onClick={() =>
                    generateSopMutation.mutate({
                      logId: id,
                      documentId: selectedManualId,
                    })
                  }
                  disabled={!selectedManualId || generateSopMutation.isPending}
                  className="btn btn-primary flex-1 h-12 shadow-lg shadow-blue-100 gap-2"
                >
                  {generateSopMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Synthesize SOP Draft
                </button>
                <button
                  onClick={() => setIsSopModalOpen(false)}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-slate-200 pb-6 pt-8">
        <div className="flex items-start gap-4">
          <Link
            to="/maintenance"
            className="p-3 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold text-slate-900">{log.title}</h1>
              <span
                className={clsx(
                  "px-3 py-1 rounded-full text-xs font-semibold border flex items-center gap-1.5",
                  log.status === "completed"
                    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                    : log.status === "in_progress"
                      ? "bg-yellow-100 text-yellow-700 border-yellow-200"
                      : "bg-blue-100 text-blue-700 border-blue-200",
                )}
              >
                <div
                  className={clsx(
                    "w-2 h-2 rounded-full",
                    log.status === "completed"
                      ? "bg-emerald-500"
                      : log.status === "in_progress"
                        ? "bg-yellow-500"
                        : "bg-blue-500",
                  )}
                />
                <span className="capitalize">
                  {log.status.replace("_", " ")}
                </span>
              </span>
            </div>
            <div className="text-sm text-slate-500 mt-3 flex items-center gap-x-4 gap-y-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Settings className="w-4 h-4" /> {log.machine_name}
              </div>
              <div className="flex items-center gap-1.5">
                <FileText className="w-4 h-4" />{" "}
                {log.sop_title || "General Maintenance"}
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" /> Created on{" "}
                {new Date(log.created_at).toLocaleDateString()}
              </div>

              {/* MODIFICATION: Assign/Reassign UI moved here */}
              <div className="flex items-center gap-1.5">
                <AnimatePresence mode="wait">
                  {isAssigning ? (
                    <motion.div
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="flex items-center gap-2 p-1.5 bg-slate-100 rounded-xl border border-slate-200"
                    >
                      <select
                        className="text-sm bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={selectedTech}
                        onChange={(e) => setSelectedTech(e.target.value)}
                      >
                        <option value="">Choose...</option>
                        {technicians?.map((tech) => (
                          <option key={tech.id} value={tech.id}>
                            {tech.firstName} {tech.lastName}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => assignMutation.mutate(selectedTech)}
                        disabled={!selectedTech || assignMutation.isPending}
                        className="p-1.5 text-slate-500 hover:bg-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Save"
                      >
                        {assignMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                        ) : (
                          <Save className="w-4 h-4 text-blue-600" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsAssigning(false);
                          setSelectedTech("");
                        }}
                        className="p-1.5 text-slate-500 hover:bg-white rounded-md"
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="display"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="flex items-center gap-1.5"
                    >
                      <User className="w-4 h-4" />
                      <span className="font-medium">
                        {log.technician_name || (
                          <span className="italic text-slate-400 font-normal">
                            Unassigned
                          </span>
                        )}
                      </span>
                      {canManage && log.status !== "completed" && (
                        <button
                          onClick={() => setIsAssigning(true)}
                          className="ml-1 text-blue-600 hover:underline text-xs font-semibold"
                        >
                          ({log.technician_id ? "Reassign" : "Assign"})
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {log.status !== "completed" && (
          <div className="w-full sm:w-auto flex-shrink-0 pt-2 sm:pt-0 pl-0 sm:pl-4">
            <Link
              to={`/maintenance/${id}/execute`}
              className="btn btn-primary gap-2 shadow-lg w-full sm:w-auto justify-center"
            >
              <Wrench className="w-4 h-4" />
              {log.status === "in_progress" ? "Continue" : "Start"} Execution
            </Link>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Flag className="w-5 h-5 text-blue-500" />
                <h3 className="font-bold text-slate-800">Original Objective</h3>
              </div>
              {canManage && log.status !== "completed" && !isUpdatingSOP && (
                <button
                  onClick={() => setIsUpdatingSOP(true)}
                  className="btn btn-ghost btn-sm text-blue-600 gap-2"
                >
                  <Edit3 className="w-4 h-4" />{" "}
                  {log.sop_id ? "Change SOP" : "Assign SOP"}
                </button>
              )}
            </div>
            <div className="p-6 space-y-6">
              {isUpdatingSOP ? (
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-3">
                  <label className="text-[10px] font-black uppercase text-slate-400">
                    Select Standard Operating Procedure
                  </label>
                  <select
                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm"
                    value={selectedSOP}
                    onChange={(e) => setSelectedSOP(e.target.value)}
                  >
                    <option value="">Choose procedure...</option>
                    {availableSOPs?.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.title}
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateSopMutation.mutate(selectedSOP)}
                      disabled={!selectedSOP || updateSopMutation.isPending}
                      className="btn btn-primary btn-sm flex-1"
                    >
                      Assign SOP
                    </button>
                    <button
                      onClick={() => setIsUpdatingSOP(false)}
                      className="btn btn-ghost btn-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 font-medium text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {log.description || "No primary description provided."}
                </div>
              )}
            </div>
          </div>

          {executionSummary && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-3xl p-6 shadow-sm">
              <h3 className="text-emerald-900 font-bold flex items-center gap-2 mb-4 text-sm uppercase tracking-wider">
                <ShieldCheck className="w-5 h-5" /> Work Performed (AI Summary)
              </h3>
              <p className="text-slate-700 italic text-sm leading-relaxed">
                "{executionSummary}"
              </p>
              {log.notes && (
                <div className="mt-4 pt-4 border-t border-emerald-100">
                  <label className="text-[9px] font-black uppercase text-emerald-400 block mb-1">
                    Technician Input
                  </label>
                  <p className="text-xs text-slate-600">{log.notes}</p>
                </div>
              )}
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-3xl shadow-sm">
            <div className="p-6 border-b border-slate-100 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-500" />
              <h3 className="font-bold text-slate-800">
                Execution Steps & Documentation
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-8">
                {stepsCompleted.length > 0 ? (
                  stepsCompleted.map((step, i) => {
                    const originalInstruction =
                      sopSteps[step.stepIndex]?.instruction ||
                      step.instruction ||
                      `Step ${i + 1}`;
                    return (
                      <div key={i} className="flex gap-4">
                        <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold border border-slate-200 flex-shrink-0">
                          {i + 1}
                        </div>
                        <div className="flex-1 space-y-3">
                          <p className="text-slate-900 font-semibold text-sm">
                            {originalInstruction}
                          </p>
                          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                            <p className="text-slate-700 text-sm">
                              {step.notes || (
                                <span className="italic opacity-50">
                                  Confirmed complete.
                                </span>
                              )}
                            </p>
                            {(step.photoUrl || step.photo) && (
                              <button
                                onClick={() =>
                                  setActivePhoto(step.photoUrl || step.photo)
                                }
                                className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100/50 border border-blue-100 text-blue-600 rounded-lg text-xs font-bold transition-all hover:bg-blue-100 hover:shadow-sm"
                              >
                                <Camera className="w-3.5 h-3.5" /> View Evidence
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-10 text-slate-400 italic text-sm">
                    No execution data recorded yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          {/* 3. Knowledge Capture Button */}
          {showStandardizeButton && (
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-6 text-white shadow-xl shadow-blue-100 relative overflow-hidden group">
              <FilePlus className="absolute -top-4 -right-4 w-24 h-24 text-white/10 rotate-12 group-hover:scale-110 transition-transform duration-500" />
              <div className="relative z-10 space-y-4">
                <h3 className="font-black text-[10px] uppercase tracking-widest text-blue-200 flex items-center gap-2">
                  <Tag className="w-3 h-3" /> Standardize Knowledge
                </h3>
                <p className="text-xs leading-relaxed font-medium">
                  This corrective fix was performed without a standard
                  procedure. Convert this successful execution into a new SOP
                  using AI.
                </p>
                <button
                  onClick={() => setIsSopModalOpen(true)}
                  className="w-full bg-white text-blue-700 py-3 rounded-xl font-bold text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-transform hover:-translate-y-1 active:scale-95"
                >
                  <Sparkles className="w-4 h-4" /> Create SOP from Fix
                </button>
              </div>
            </div>
          )}

          {/* Render AI Insights only if the ticket originated from a Bug/Error */}
          {log?.bug_id && aiPlan?.suggestion && (
            <div className="bg-slate-900 rounded-3xl p-6 text-white shadow-xl relative overflow-hidden border border-white/5">
              <Sparkles className="absolute -top-4 -right-4 w-24 h-24 text-white/10 rotate-12" />
              <div className="relative z-10 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-black text-[10px] uppercase tracking-widest text-blue-400 flex items-center gap-2">
                    <Sparkles className="w-3 h-3" /> AI Suggested Plan
                  </h3>
                  <span className="bg-blue-500/20 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-tighter">
                    {Math.round((aiPlan.confidence_score || 0) * 100)}% Match
                  </span>
                </div>
                <div className="text-sm leading-relaxed text-slate-200 font-medium whitespace-pre-wrap border-l-2 border-blue-500/30 pl-4 py-1">
                  {aiPlan.suggestion}
                </div>
                <div className="space-y-2 pt-4 border-t border-white/10">
                  <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                    Evidence Sources
                  </p>
                  <div className="grid grid-cols-1 gap-1.5">
                    {aiPlan.references?.map((ref, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-white/5 p-2 rounded-xl"
                      >
                        {ref.type === "manual" ? (
                          <FileText className="w-3 h-3 text-blue-400" />
                        ) : (
                          <History className="w-3 h-3 text-amber-400" />
                        )}
                        <span className="text-[10px] font-bold text-slate-300 truncate">
                          {ref.title}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {linkedBug && (
            <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
              <div className="p-4 bg-rose-50 border-b border-rose-100 flex items-center justify-between text-rose-800">
                <div className="flex items-center gap-2 font-bold text-[10px] uppercase tracking-widest">
                  <AlertTriangle className="w-4 h-4" /> Error Log
                </div>
                <span className="bg-rose-600 text-white text-[9px] font-black px-2 py-0.5 rounded-full uppercase">
                  Ref: {log.bug_id.substring(0, 5)}
                </span>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-slate-800 text-sm font-medium leading-relaxed italic">
                  "{linkedBug.description}"
                </p>
                {linkedBug.mediaUrls?.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {linkedBug.mediaUrls.map((url, idx) => (
                      <div
                        key={idx}
                        onClick={() => setActivePhoto(url)}
                        className="aspect-square rounded-xl overflow-hidden border border-slate-100 cursor-zoom-in hover:brightness-90 transition-all"
                      >
                        <img
                          src={url}
                          className="w-full h-full object-cover"
                          alt="Bug Evidence"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MODIFICATION: "Responsibility" card now only shows Parts */}
          <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-6">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-slate-400" /> Parts Identification
            </h3>
            <div className="flex flex-wrap gap-2">
              {partsUsed.length > 0 ? (
                partsUsed.map((part, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-[10px] font-black border border-blue-100 shadow-sm"
                  >
                    {part}
                  </span>
                ))
              ) : (
                <span className="text-slate-400 text-xs italic">
                  No parts consumed.
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceDetail;
